import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Plus, Trash2, Edit2, Save, X, DollarSign, Globe } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Common currency options for quick selection
const CURRENCY_PRESETS = [
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CHF', symbol: 'Fr', name: 'Swiss Franc' },
];

const AdminPricing = () => {
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCode, setEditingCode] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newPricing, setNewPricing] = useState({
    country_code: '',
    country: '',
    currency: 'USD',
    symbol: '$',
    price: '',
  });
  const [saving, setSaving] = useState(false);

  const adminAuth = localStorage.getItem('adminAuth');

  const fetchPricing = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/pricing`, {
        headers: { 'Authorization': `Basic ${adminAuth}` }
      });
      if (!res.ok) throw new Error('Failed to fetch pricing');
      const data = await res.json();
      setPricing(data);
    } catch (err) {
      toast.error('Failed to load pricing data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [adminAuth]);

  useEffect(() => {
    fetchPricing();
  }, [fetchPricing]);

  const startEdit = (item) => {
    setEditingCode(item.country_code);
    setEditForm({ ...item });
  };

  const cancelEdit = () => {
    setEditingCode(null);
    setEditForm({});
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/pricing/${editingCode}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Basic ${adminAuth}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          country: editForm.country,
          currency: editForm.currency,
          symbol: editForm.symbol,
          price: parseFloat(editForm.price)
        })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to update');
      }
      toast.success('Pricing updated successfully');
      setEditingCode(null);
      setEditForm({});
      fetchPricing();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (countryCode) => {
    if (!window.confirm(`Delete pricing for ${countryCode}? Visitors from this country will see the default price.`)) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/pricing/${countryCode}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Basic ${adminAuth}` }
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to delete');
      }
      toast.success('Pricing deleted');
      fetchPricing();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleCurrencyPreset = (preset, isNew = false) => {
    if (isNew) {
      setNewPricing(prev => ({ ...prev, currency: preset.code, symbol: preset.symbol }));
    } else {
      setEditForm(prev => ({ ...prev, currency: preset.code, symbol: preset.symbol }));
    }
  };

  const handleAddPricing = async (e) => {
    e.preventDefault();
    if (!newPricing.country_code || !newPricing.country || !newPricing.price) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/pricing`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${adminAuth}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...newPricing,
          price: parseFloat(newPricing.price)
        })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to add pricing');
      }
      toast.success('Pricing added successfully');
      setShowAddForm(false);
      setNewPricing({ country_code: '', country: '', currency: 'USD', symbol: '$', price: '' });
      fetchPricing();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Geo-based Pricing</h1>
          <p className="text-gray-600 mt-1">Manage Instant Itinerary prices for different countries</p>
        </div>
        <Button onClick={() => setShowAddForm(true)} className="bg-amber-600 hover:bg-amber-700" data-testid="add-pricing-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Country
        </Button>
      </div>

      {/* Add New Pricing Form */}
      {showAddForm && (
        <Card className="border-amber-200 bg-amber-50" data-testid="add-pricing-form">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Add New Country Pricing
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAddPricing} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div>
                  <Label htmlFor="country_code">Country Code *</Label>
                  <Input
                    id="country_code"
                    placeholder="e.g., CA, JP"
                    value={newPricing.country_code}
                    onChange={(e) => setNewPricing(prev => ({ ...prev, country_code: e.target.value.toUpperCase() }))}
                    maxLength={2}
                    className="uppercase"
                    data-testid="new-country-code"
                  />
                  <p className="text-xs text-gray-500 mt-1">ISO 3166-1 alpha-2</p>
                </div>
                <div>
                  <Label htmlFor="country">Country Name *</Label>
                  <Input
                    id="country"
                    placeholder="e.g., Canada, Japan"
                    value={newPricing.country}
                    onChange={(e) => setNewPricing(prev => ({ ...prev, country: e.target.value }))}
                    data-testid="new-country-name"
                  />
                </div>
                <div>
                  <Label>Currency</Label>
                  <select
                    className="w-full h-10 px-3 border rounded-md bg-white"
                    value={newPricing.currency}
                    onChange={(e) => {
                      const preset = CURRENCY_PRESETS.find(p => p.code === e.target.value);
                      if (preset) handleCurrencyPreset(preset, true);
                    }}
                    data-testid="new-currency"
                  >
                    {CURRENCY_PRESETS.map(p => (
                      <option key={p.code} value={p.code}>{p.symbol} {p.code} - {p.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="symbol">Symbol</Label>
                  <Input
                    id="symbol"
                    value={newPricing.symbol}
                    onChange={(e) => setNewPricing(prev => ({ ...prev, symbol: e.target.value }))}
                    data-testid="new-symbol"
                  />
                </div>
                <div>
                  <Label htmlFor="price">Price *</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="e.g., 2.50"
                    value={newPricing.price}
                    onChange={(e) => setNewPricing(prev => ({ ...prev, price: e.target.value }))}
                    data-testid="new-price"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={saving} className="bg-green-600 hover:bg-green-700" data-testid="save-new-pricing">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowAddForm(false)} data-testid="cancel-new-pricing">
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Pricing Table */}
      <Card data-testid="pricing-table">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            All Country Pricing ({pricing.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Code</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Country</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Currency</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Symbol</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Price</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Display</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {pricing.map((item) => (
                  <tr key={item.country_code} className={item.is_default ? 'bg-blue-50' : 'hover:bg-gray-50'} data-testid={`pricing-row-${item.country_code}`}>
                    {editingCode === item.country_code ? (
                      <>
                        <td className="px-4 py-3">
                          <span className="font-mono font-bold text-gray-900">{item.country_code}</span>
                          {item.is_default && <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">Default</span>}
                        </td>
                        <td className="px-4 py-3">
                          <Input
                            value={editForm.country}
                            onChange={(e) => setEditForm(prev => ({ ...prev, country: e.target.value }))}
                            className="w-full max-w-[200px]"
                            data-testid={`edit-country-${item.country_code}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <select
                            className="h-10 px-2 border rounded-md bg-white"
                            value={editForm.currency}
                            onChange={(e) => {
                              const preset = CURRENCY_PRESETS.find(p => p.code === e.target.value);
                              if (preset) handleCurrencyPreset(preset);
                            }}
                            data-testid={`edit-currency-${item.country_code}`}
                          >
                            {CURRENCY_PRESETS.map(p => (
                              <option key={p.code} value={p.code}>{p.code}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          <Input
                            value={editForm.symbol}
                            onChange={(e) => setEditForm(prev => ({ ...prev, symbol: e.target.value }))}
                            className="w-20"
                            data-testid={`edit-symbol-${item.country_code}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={editForm.price}
                            onChange={(e) => setEditForm(prev => ({ ...prev, price: e.target.value }))}
                            className="w-24"
                            data-testid={`edit-price-${item.country_code}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-lg font-semibold text-amber-600">
                            {editForm.symbol}{parseFloat(editForm.price || 0).toFixed(2)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <Button size="sm" onClick={saveEdit} disabled={saving} className="bg-green-600 hover:bg-green-700" data-testid={`save-edit-${item.country_code}`}>
                              <Save className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={cancelEdit} data-testid={`cancel-edit-${item.country_code}`}>
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-3">
                          <span className="font-mono font-bold text-gray-900">{item.country_code}</span>
                          {item.is_default && <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">Default</span>}
                        </td>
                        <td className="px-4 py-3 text-gray-700">{item.country}</td>
                        <td className="px-4 py-3 text-gray-600">{item.currency}</td>
                        <td className="px-4 py-3 text-gray-600">{item.symbol}</td>
                        <td className="px-4 py-3 text-gray-700">{item.price}</td>
                        <td className="px-4 py-3">
                          <span className="text-lg font-semibold text-amber-600">
                            {item.symbol}{item.price % 1 === 0 ? item.price : item.price.toFixed(2)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <Button size="sm" variant="outline" onClick={() => startEdit(item)} data-testid={`edit-btn-${item.country_code}`}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            {!item.is_default && (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => handleDelete(item.country_code)}
                                data-testid={`delete-btn-${item.country_code}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {pricing.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No pricing configured. Add your first country pricing above.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-gray-50 border-gray-200">
        <CardContent className="p-4">
          <h3 className="font-semibold text-gray-700 mb-2">How Geo-Pricing Works</h3>
          <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
            <li>When a visitor opens the Instant Itinerary page, their country is detected via IP address</li>
            <li>If their country has a specific price configured, they see that price</li>
            <li>If their country is not listed, they see the <strong>Default</strong> price (marked in blue)</li>
            <li>The Default pricing cannot be deleted, only edited</li>
            <li>Use 2-letter ISO country codes (e.g., US, GB, IN, AU)</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPricing;
